import React, { useCallback, useState } from 'react';

import Link from '@/components/Link';

import { ConsultationCard } from './ConsultationCard';
import type { ConsultationCardItem } from './ConsultationCard';
import { DashboardCard } from './DashboardCard';
import { ExpertAvatar, ProjectChip } from './primitives';
import { dashboardCurrency } from './useDashboardData';
import type { DashboardContext, ExpertConsultation, ExpertSummary } from './useDashboardData';

interface MyExpertsCardProps {
  experts: ExpertSummary[];
  /**
   * Accurate count of all distinct experts the viewer has consulted. Drives the
   * badge; `experts` is the recent preview rendered in the card body, so the
   * badge can exceed the rows shown. ENG-5253.
   */
  total: number;
  unreviewedCount: number;
  dashCtx: DashboardContext;
  loading: boolean;
  error?: boolean;
  // When the team has an internal network, the "View full network" link points
  // there. Without one, fall back to the global search.
  internalNetworkId?: string;
  collapseButton?: React.ReactNode;
  collapsed?: boolean;
}

export function MyExpertsCard({
  experts,
  total,
  unreviewedCount,
  dashCtx,
  loading,
  error,
  internalNetworkId,
  collapseButton,
  collapsed,
}: MyExpertsCardProps): React.ReactNode {
  const attentionBadge =
    unreviewedCount > 0 ? (
      <span className="inline-flex items-center rounded-full bg-yellow-500/15 px-8 py-2 text-[1.1rem] font-semibold text-yellow-800 dark:text-yellow-300">
        {unreviewedCount} needs review
      </span>
    ) : undefined;

  const networkLink = internalNetworkId ? `/search?networks[]=${internalNetworkId}` : '/search';
  // The badge counts the experts this user has consulted; the link goes to the
  // wider expert network (a different, larger population). Label it as a
  // discovery action so the two aren't read as the same count. ENG-5253.
  const networkLinkText = internalNetworkId ? 'Browse expert network' : 'Find more experts';

  return (
    <DashboardCard
      title="My Experts"
      badge={total}
      attentionBadge={attentionBadge}
      linkText={networkLinkText}
      linkTo={networkLink}
      loading={loading}
      error={error}
      fullWidth
      collapseButton={collapseButton}
      collapsed={collapsed}
    >
      {experts.length === 0 ? (
        <p className="text-12 text-on-surface-muted">No experts consulted yet.</p>
      ) : (
        <div className="-mx-16 -mb-16 overflow-hidden rounded-b-lg border-t border-surface-border">
          {experts.map((expert) => (
            <ExpertRow key={expert.id} expert={expert} dashCtx={dashCtx} defaultExpanded={false} />
          ))}
        </div>
      )}
    </DashboardCard>
  );
}

function ExpertRow({
  expert,
  dashCtx,
  defaultExpanded,
}: {
  expert: ExpertSummary;
  dashCtx: DashboardContext;
  defaultExpanded: boolean;
}): React.ReactNode {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const toggle = useCallback(() => setExpanded((prev) => !prev), []);

  return (
    <div className="last:border-b-0 border-b border-surface-border">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        className="flex w-full cursor-pointer flex-wrap items-center gap-x-10 gap-y-8 border-none bg-transparent px-16 py-12 text-left transition-colors hover:bg-surface-elevated"
      >
        <svg
          className={`h-[1.4rem] w-[1.4rem] shrink-0 text-on-surface-muted transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>

        <ExpertAvatar
          firstName={expert.firstName}
          lastName={expert.lastName}
          name={expert.name}
          pictureUrl={expert.pictureUrl}
          size={40}
        />

        <div className="min-w-0 flex-1 max-lg:basis-full">
          <div className="flex items-center gap-6">
            {expert.htmlUrl ? (
              <Link
                to={expert.htmlUrl}
                className="text-14 font-semibold text-brand-primary no-underline hover:underline"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                {expert.name}
              </Link>
            ) : (
              <span className="text-14 font-semibold text-on-surface">{expert.name}</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {expert.headline && (
              <span className="truncate text-12 text-on-surface-muted">{expert.headline}</span>
            )}
            {expert.headline && expert.projectNames.length > 0 && (
              <span className="text-12 text-on-surface-subtle">&middot;</span>
            )}
            <div className="flex flex-wrap gap-4">
              {expert.projectNames.map((name) => (
                <ProjectChip key={name} name={name} />
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-16 gap-y-4 text-12 text-on-surface-muted">
          <span className="flex items-center gap-4 whitespace-nowrap">
            <svg
              className="h-[1.3rem] w-[1.3rem] opacity-60"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <strong className="font-semibold text-on-surface">{expert.consultationCount}</strong>{' '}
            completed
          </span>
          <span className="flex items-center gap-4 whitespace-nowrap">
            <svg
              className="h-[1.3rem] w-[1.3rem] opacity-60"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <strong className="font-semibold text-on-surface">{expert.scheduledCount}</strong>{' '}
            scheduled
          </span>
        </div>

        <span className="flex items-center gap-6">
          <Link
            to="/messaging"
            className="inline-flex shrink-0 items-center rounded-md border border-surface-border bg-surface-elevated px-10 py-4 text-[1.1rem] font-semibold text-on-surface-secondary no-underline shadow-5 transition-shadow hover:shadow-1"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            Message
          </Link>
          <Link
            to={expert.htmlUrl ? `${expert.htmlUrl}/request` : '/search'}
            className="inline-flex shrink-0 items-center rounded-md border border-brand-primary bg-brand-primary px-10 py-4 text-[1.1rem] font-semibold text-surface-card no-underline shadow-5 transition-shadow hover:shadow-1"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            Book Call
          </Link>
        </span>
      </button>

      <div
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
          expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="bg-surface-elevated px-16 py-12">
            {expert.projectNames.length > 0 && (
              <div className="mb-10 flex flex-wrap items-center gap-x-6 gap-y-4 text-10 font-semibold uppercase tracking-wider text-on-surface-muted">
                <span>Engagement History</span>
                <span>&middot;</span>
                <span>Projects:</span>
                {expert.projectNames.map((name) => (
                  <ProjectChip key={name} name={name} />
                ))}
              </div>
            )}
            <div className="flex flex-col gap-8" role="list">
              {expert.consultations.map((c) => (
                <ConsultationCard
                  key={c.id}
                  consultation={toItem(c, expert)}
                  currency={dashboardCurrency(dashCtx)}
                  layout="row"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function toItem(c: ExpertConsultation, expert: ExpertSummary): ConsultationCardItem {
  return {
    id: c.id,
    htmlUrl: c.htmlUrl,
    note: c.title,
    date: c.date,
    durationMinutes: c.durationMinutes,
    costCents: c.costCents,
    engagementType: c.engagementType,
    projectName: c.projectName,
    rating: c.rating,
    hasReview: c.hasReview,
    canReview: c.canReview,
    valueTags: c.valueTags,
    expert: {
      name: expert.name,
      firstName: expert.firstName,
      lastName: expert.lastName,
      pictureUrl: expert.pictureUrl,
    },
  };
}
